import { PasswordInput } from "@/components/app-ui/password-input";
import { FieldError } from "@/components/shadcn/field";
import {
    getPasswordRequirementResults,
    PasswordComplexityMessages,
} from "@/lib/auth/password-policy";
import { cn } from "cn";
import { Check, Lock, X } from "lucide-react";
import type { ComponentProps } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { FormFieldBase, FormFieldCommonProps } from "./form-field-base";

const DEFAULT_PASSWORD_PLACEHOLDER = "• • • • • • • • • •";

type PasswordInputFormFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldCommonProps<TFieldValues, TName> & {
    placeholder?: string;
    autoComplete?: ComponentProps<typeof PasswordInput>["autoComplete"];
    passwordRequirements?: PasswordComplexityMessages;
};

export function PasswordInputFormField<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    placeholder = DEFAULT_PASSWORD_PLACEHOLDER,
    autoComplete,
    passwordRequirements,
    ...props
}: PasswordInputFormFieldProps<TFieldValues, TName>) {
    return (
        <FormFieldBase
            {...props}
            renderField={(field, fieldState, disabled) => (
                <div className="relative">
                    <Lock
                        aria-hidden="true"
                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2"
                    />
                    <PasswordInput
                        id={field.name}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        aria-invalid={fieldState.invalid}
                        disabled={disabled}
                        className="pl-9"
                        {...field}
                    />
                </div>
            )}
            renderError={(field, fieldState, formState) => {
                if (!passwordRequirements) {
                    return (
                        fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                        )
                    );
                }

                const requirements = getPasswordRequirementResults(
                    String(field.value ?? ""),
                    passwordRequirements,
                );
                const hasSubmitted = formState.submitCount > 0;
                const requirementMessages = new Set(
                    requirements.map(({ message }) => message),
                );
                const nonRequirementError =
                    fieldState.error?.message &&
                    !requirementMessages.has(fieldState.error.message)
                        ? fieldState.error
                        : undefined;

                return (
                    <div
                        aria-live="polite"
                        className="flex flex-col gap-1 text-sm"
                    >
                        {nonRequirementError && (
                            <FieldError errors={[nonRequirementError]} />
                        )}
                        <ul className="flex flex-col gap-1">
                            {requirements.map(({ key, message, isMet }) => (
                                <li
                                    key={key}
                                    data-state={
                                        isMet
                                            ? "met"
                                            : hasSubmitted
                                              ? "unmet"
                                              : "idle"
                                    }
                                    className={cn(
                                        "flex items-center gap-1.5 transition-colors",
                                        isMet
                                            ? "text-success"
                                            : hasSubmitted
                                              ? "text-destructive"
                                              : "text-foreground",
                                    )}
                                >
                                    <span
                                        data-slot="password-requirement-icon"
                                        className={cn(
                                            "flex size-5 shrink-0 items-center justify-center",
                                            isMet
                                                ? "bg-success/10 text-success rounded-full"
                                                : hasSubmitted
                                                  ? "bg-destructive/10 text-destructive rounded-full"
                                                  : undefined,
                                        )}
                                    >
                                        {isMet ? (
                                            <Check
                                                aria-hidden="true"
                                                className="size-3"
                                            />
                                        ) : hasSubmitted ? (
                                            <X
                                                aria-hidden="true"
                                                className="size-3"
                                            />
                                        ) : null}
                                    </span>
                                    <span>{message}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            }}
        />
    );
}
